import { useCallback, useEffect, useMemo } from 'react';
import {
  OneOfElementListChildren,
  OneOfElementListElements,
  OneOfParentElements,
} from '@shared/models/project.model';
import {
  getSelectedVersion,
  useSelectedVersionProducts,
} from '@client/store/ui';
import { useRootElementChildren } from './use-element-category.hooks';
import { createSortFilterStore } from './use-filter.hook';
import {
  getElementName,
  shouldHideProductElement,
} from '@shared/helpers/element.helpers';
import {
  SortByAlternativeKey,
  SortByFn,
  SortDirection,
  getPathInFlatTree,
} from '@shared/helpers/tree.helpers';
import { EMPTY_FROZEN_ARRAY } from '@shared/helpers/array.helpers';
import {
  flattenElements,
  isBuildingVersionElement,
  isElement,
  isDeletedElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import { createLocalStorageStore } from '../helpers/local-storage.helpers';
import {
  useSelectedOrganizationName,
  getSelectedOrganization,
} from '@client/store/organization';
import { getShowElementListProductRows } from '@shared/helpers/organization.helpers';
import { searchFilter } from '@shared/helpers/string.helpers';
import { useElementMaxResultsRecord } from '@features/proposals/use-proposals.hooks';
import { useSearchParam } from './use-search-param.hook';
import { ProductRecord } from '@shared/models/product.model';
import { genericProductsLookup } from '@shared/generic-products';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { useElementListGroupBy } from '@features/elements/helpers/element-grouping.helpers';
import { getElementGroupingHelpers } from '@features/elements/element-grouping.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { useEffectiveSbefGroups } from '@features/sbef/use-effective-sbef-groups.hook';
import {
  getElementSbefGroupKey,
  isSbefGroupFilterActive,
} from '@shared/helpers/sbef-results.helpers';
import { getUser, useUser } from '../hooks/use-user.hooks';

type SortElementsOptionKey = 'co2' | 'cost' | 'position';

export type SortElementsOptions = OneOfElementListElements &
  Record<SortElementsOptionKey, any>;

export const {
  useStore: useSortElementsBy,
  set: setSortElementsBy,
  get: getSortElementsBy,
} = createLocalStorageStore<SortByAlternativeKey<SortElementsOptions>>(
  'sort_elements_by',
  'name',
);

export const useElementSearchString = () => useSearchParam('search', '');

export const {
  useStore: useSortElementsDirection,
  set: setSortElementsDirection,
} = createLocalStorageStore<SortDirection>(
  'sort_elements_direction',
  getSortElementsBy() === 'co2' || getSortElementsBy() === 'cost'
    ? 'desc'
    : 'asc',
);

const ELEMENT_VERSION_GROUP_SORT_KEY = 'version-id-sort';
const ELEMENT_SORT_KEY = 'element-sort';
const ADDED_ELEMENT_SORT_KEY = 'added-element-sort';

const ID_COMPARE_FN = (
  a: OneOfElementListElements,
  b: OneOfElementListElements,
) => a.id === b.id;

const {
  setSortingOption,
  setFilter,
  useSortFilteredItems,
  useSortFilteredChildren,
} = createSortFilterStore<OneOfElementListChildren>({
  childrenKey: 'elements',
  filters: [
    // No generated elements with 0 count
    {
      id: 'generated-products-filter',
      function: (item) => !shouldHideProductElement(item),
    },
    // Never show deleted elements (like temp element from EditProductDialog)
    {
      id: 'deleted-elements-filter',
      function: (el, path) => [...path, el].every((e) => !isDeletedElement(e)),
    },
    // Product rows visibility controlled by organization metadata flag
    {
      id: 'admin-EL-product-row-visibility',
      function: (item) => {
        if (!isProductElement(item)) return true;
        const user = getUser();
        const selectedOrgName = getSelectedOrganization();
        const selectedOrg = user?.organizations.find(
          ({ name }) => name === selectedOrgName,
        );
        return getShowElementListProductRows(selectedOrg);
      },
    },
  ],
  sorting: [
    // Always group by version category (this is just a placeholder to prio this first)
    {
      id: ELEMENT_VERSION_GROUP_SORT_KEY,
      disabled: true,
    },
    // Sort elements within the same version group
    {
      id: ELEMENT_SORT_KEY,
      disabled: true,
    },
    // Placeholder to always keep added element at the top
    {
      id: ADDED_ELEMENT_SORT_KEY,
      disabled: true,
    },
  ],
  name: 'ElementListSorting',
  compareFn: ID_COMPARE_FN,
});

/**
 * Get all elements in the selected version in a
 */
export const useFlatSortFilteredElements = () => {
  const elements = useRootElementChildren();
  return useSortFilteredItems(elements);
};

/**
 * Most performant way to get the path to an element
 * @param element
 * @returns
 */
export const getPathFromFlattenedElements = (
  element: OneOfElementListElements,
): OneOfParentElements[] => {
  const version = getSelectedVersion();
  if (!version) {
    return EMPTY_FROZEN_ARRAY as OneOfParentElements[];
  }

  return getPathInFlatTree(
    flattenElements(version),
    element,
    'elements',
    ID_COMPARE_FN,
  ) as OneOfParentElements[];
};

/**
 * Initialize filters and sorting for elements.
 * Should be called once in the root element list component
 */
export const useInitElementsSortFilter = () => {
  useInitElementsFilter();
  useInitElementsSorting();
};

const filterAll = () => true;

/**
 * Initialize filters for elements.
 * Will filter out main category elements since they are show as icons
 * Will filter out elements that are not the selected main category element
 */
const useInitElementsFilter = () => {
  const elements = useRootElementChildren();
  const [searchString] = useElementSearchString();
  const [grouping] = useElementListGroupBy();
  const groupingHelpers = getElementGroupingHelpers(grouping)?.helpers;
  const selectedOrg = useSelectedOrganizationName();
  const user = useUser();
  const products = useSelectedVersionProducts();
  const effectiveSbefGroups = useEffectiveSbefGroups();
  const searchableTextById = useMemo(() => {
    const map = new Map<string, string>();

    const buildSearchText = (
      item: OneOfElementListChildren,
      path: OneOfElementListChildren[],
    ) => {
      const name = getElementName(item, products);
      const pathNames = path.map((parent) => {
        if (!isElement(parent)) return '';
        return parent.name || parent.fallbackName || '';
      });
      const childNames = isElement(item)
        ? (item.elements ?? []).map((child) => getElementName(child, products))
        : [];
      const groupLabel = groupingHelpers?.getSearchLabel(item, path);

      map.set(
        item.id,
        [name, ...childNames, ...pathNames, groupLabel]
          .filter(guards.is.defined)
          .join(' ')
          .toLowerCase(),
      );

      if (!isElement(item)) return;
      const nextPath = [...path, item];
      item.elements.forEach((child) => buildSearchText(child, nextPath));
    };

    elements.forEach((element) => buildSearchText(element, []));
    return map;
  }, [elements, products, groupingHelpers]);
  // Proposal visibility is already baked into the element tree after migration.
  useEffect(() => {
    setFilter({
      id: 'inactive-elements-filter',
      function: filterAll,
    });
  }, []);

  // Show elements and its children that match the search string
  useEffect(() => {
    setFilter({
      id: 'search-filter-by-name',
      function: (item) => {
        const searchText = searchableTextById.get(item.id) ?? '';
        return searchFilter(searchText, searchString);
      },
    });
  }, [searchString, searchableTextById]);

  // SBEF group filter: keep a row when the shallowest element ancestor on the walk
  // from the list root ([...path, item]) belongs to a selected group. That ancestor
  // groups the whole subtree below it (e.g. a floor keeps walls and products under it).
  // Empty or all-groups selection shows every row.
  useEffect(() => {
    setFilter({
      id: 'sbef-group-filter',
      function: (item, path) => {
        if (!isSbefGroupFilterActive(effectiveSbefGroups)) return true;
        const root = [...path, item].find(isElement);
        if (!root) return true;
        const groupKey = getElementSbefGroupKey(root);
        return groupKey != null && effectiveSbefGroups.includes(groupKey);
      },
    });
  }, [effectiveSbefGroups]);

  // Re-apply product visibility filter when org or user changes to trigger recompute
  useEffect(() => {
    setFilter({
      id: 'admin-EL-product-row-visibility',
      function: (item) => {
        if (!isProductElement(item)) return true;
        const user = getUser();
        const selectedOrgName = getSelectedOrganization();
        const selectedOrg = user?.organizations.find(
          ({ name }) => name === selectedOrgName,
        );
        return getShowElementListProductRows(selectedOrg);
      },
    });
  }, [selectedOrg, user?.organizations]);
};

/**
 * Initialize sorting for elements.
 * Apply the different sorting methods and make sure they update when dependencies change
 */
const useInitElementsSorting = () => {
  const direction = useSortElementsDirection();
  const sortFn = useElementSortFn();
  const sortElementVersionGroupsFn = useSortElementVersionGroupsFn();
  // const addedElementSortFn = useAddedElementSortFn();

  // Sort element version groups (elements with same element version id)
  useEffect(() => {
    setSortingOption({
      id: ELEMENT_VERSION_GROUP_SORT_KEY,
      property: sortElementVersionGroupsFn,
      direction,
    });
  }, [sortElementVersionGroupsFn, direction]);

  // Place added element at the top
  // useEffect(() => {
  //   setSortingOption({
  //     id: ADDED_ELEMENT_SORT_KEY,
  //     property: addedElementSortFn,
  //   });
  // }, [addedElementSortFn, direction]);

  // Sort within element version groups
  useEffect(() => {
    setSortingOption({ id: ELEMENT_SORT_KEY, property: sortFn, direction });
  }, [direction, sortFn]);
};

/**
 * Sort fn that returns element value based on sortBy option
 * @returns
 */
const useElementSortFn = <
  T extends OneOfElementListElements,
>(): SortByFn<T> => {
  const sortBy = useSortElementsBy();
  const products = useSelectedVersionProducts();
  const byProperty = sortBy === 'co2' ? 'co2e' : 'sek';
  const maxResultsRecord = useElementMaxResultsRecord(byProperty);

  return useCallback(
    (element: T, index: number) => {
      switch (sortBy) {
        case 'name':
          return getElementSortingName(element, products);
        case 'co2':
        case 'cost': {
          return (
            getConversionFactorValue(
              maxResultsRecord[element.id],
              byProperty,
            ) ?? 0
          );
        }
        case 'position':
          return index;
      }
    },
    [sortBy, products, maxResultsRecord, byProperty],
  );
};

/**
 * Get the name of an element for sorting (should include SBEF code if available)
 * @param element
 * @param productRecord
 * @returns
 */
const getElementSortingName = (
  element: OneOfElementListElements,
  productRecord: ProductRecord = genericProductsLookup,
): string => {
  const name = getElementName(element, productRecord);
  return name.toLowerCase();

  // Restore if sbef should be taken into account when sorting elements
  // let sbefCode = getSbefCodeFromElementOrRecipe(element);

  // if (Number(sbefCode) > 100) {
  //   sbefCode = '99' + sbefCode;
  // }

  // return (sbefCode ? `${sbefCode} ${name}` : name).toLowerCase();
};

const useSortElementVersionGroupsFn = <
  T extends OneOfElementListElements,
>(): SortByFn<T> => {
  const sortFn = useElementSortFn<T>();
  return sortFn;
};

/**
 * Get all children of an element that should be visible according to current filters
 * @param element
 * @returns
 */
export const useFilteredElementChildren = (
  element?: OneOfElementListElements,
) => {
  const elements = useRootElementChildren();

  return useSortFilteredChildren(
    elements,
    isBuildingVersionElement(element) ? undefined : element, // version is root element
  );
};
